import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColumnState } from 'ag-grid-community';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { PUR5010_R01TabType } from './Store_PUR5010_R01';
import usePUR5010_R01Store from './Store_PUR5010_R01';
import { aggridHeaderClass } from 'src/components/common/common';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import {
    PUR5010_R01WarehouseSubtotals, 
    PUR5010_R01BrandSubtotals,
    mergeDataWithSubtotals,
    SUBTOTAL_ROW_CLASS_RULES,
    isSubtotalRow,
} from './utils/subtotalUtils';
import { PUR5010_R01_TABS, getDetailRowId } from './utils/tabConfig';
import { defaultPUR5010_R01Dto, PUR5010_R01Dto } from './Dto_PUR5010_R01';

const BOTTOM_LABEL_COLS = new Set(['itemCd', 'workplaceNm', 'compNm']);

const normalizeHeaderClass = (defs: ColDef[]): ColDef[] =>
	defs.map((def: ColDef & { children?: ColDef[] }) => {
		const mergedHeaderClass = [aggridHeaderClass, def.headerClass].filter(Boolean).join(' ');
		const children = Array.isArray(def.children) && def.children.length > 0
			? normalizeHeaderClass(def.children)
			: undefined;
		return { ...def, headerClass: mergedHeaderClass, suppressMenu: true, children };
	});

const applyPinnedCenterAlign = (defs: ColDef[]): ColDef[] =>
	defs.map((def: ColDef & { colId?: string; cellStyle?: ColDef['cellStyle'] }) => {
		if (!def?.colId || !BOTTOM_LABEL_COLS.has(def.colId)) return def;
		const prevCellStyle = def.cellStyle;
		const cellStyle = (params: { node?: { rowPinned?: string | null } }) => {
			if (params?.node?.rowPinned === 'bottom') {
				return {
					...(typeof prevCellStyle === 'function' ? prevCellStyle(params as never) : prevCellStyle),
					textAlign: 'center',
				};
			}
			return typeof prevCellStyle === 'function' ? prevCellStyle(params as never) : prevCellStyle;
		};
		return { ...def, cellStyle };
	});

interface StockGridBaseProps {
    gridRef: React.RefObject<AgGridReact<any> | null>;
    tabType: PUR5010_R01TabType;
    data: any[];
    columns: ColDef[];
    onTabChange: (tab: PUR5010_R01TabType) => Promise<void>;
}

export const Table_PUR5010_R01: React.FC<StockGridBaseProps> = ({
    gridRef,
    tabType,
    data,
    columns,
    onTabChange
}) => {
    const {
        PUR5010_R01activeTab,
        PUR5010_R01tabColumnStates,
        setPUR5010_R01TabColumnState,
        PUR5010_R01tabSelectedIds,
        setPUR5010_R01TabSelectedIds,
        PUR5010_R01showSubtotals,
        setPUR5010_R01TabSubtotalData,
        PUR5010_R01tabGridData,
        setPUR5010_R01TabGridData,
        PUR5010_R01tabScrollTop,
        setPUR5010_R01TabScrollTop,
    } = usePUR5010_R01Store();

    // AG Grid 충돌 방지를 위한 키
    const [gridKey, setGridKey] = useState(0);
    const [, setHoveredRowIndex] = useState<number | null>(null);
    
    // 탭 변경 시 AG Grid를 완전히 새로 생성
    useEffect(() => {
        setGridKey(prev => prev + 1);
    }, [tabType]);

    // 소계 데이터 계산 (메모이제이션 적용)
    const subtotals = useMemo(() => {
        if (!data || data.length === 0 || tabType === 'item') {
            return [];
        }
        
        switch (tabType) {
            case 'warehouse':
                return PUR5010_R01WarehouseSubtotals(data);
            case 'brand':
                return PUR5010_R01BrandSubtotals(data);
            default:
                return [];
        }
    }, [data, tabType]);

    // 그리드 데이터 생성 (메모이제이션 적용)
    const gridData = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }
        
        return mergeDataWithSubtotals(data, subtotals, PUR5010_R01showSubtotals, tabType);
    }, [data, subtotals, PUR5010_R01showSubtotals, tabType]);

    // 소계 데이터와 그리드 데이터를 상태에 저장
    useEffect(() => {
        setPUR5010_R01TabSubtotalData(tabType, subtotals);
        setPUR5010_R01TabGridData(tabType, gridData);
    }, [subtotals, gridData, tabType, setPUR5010_R01TabSubtotalData, setPUR5010_R01TabGridData]);

    
    const localStorageKey = useMemo(() => `PUR5010_R01_${tabType}_columns`, [tabType]);
    const handleTabClick = useCallback(async (tab: PUR5010_R01TabType) => {
        if (tab === PUR5010_R01activeTab) return; // 같은 탭 클릭 시 무시
        // 부모 컴포넌트의 onTabChange 호출
        await onTabChange(tab);
    }, [onTabChange, PUR5010_R01activeTab]);

    // 행 ID 생성 함수 - 소계/상세 행 모두 탭별 유니크 키 사용
    const getRowId = useCallback((params: any) => {
        const row = params.data;
        if (isSubtotalRow(row)) {
            return `subtotal_${row?.groupKey || row?.itemCd || 'EMPTY'}`;
        }
        return getDetailRowId(tabType, row);
    }, [tabType]);

    // 데이터 객체만 받아 동일 규칙으로 ID 생성
    const makeRowId = useCallback((row: any) => {
        if (isSubtotalRow(row)) {
            return `subtotal_${row?.groupKey || row?.itemCd || 'EMPTY'}`;
        }
        return getDetailRowId(tabType, row);
    }, [tabType]);

    const baseColumnDefs = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(),
        ...columns,
    ], [columns]);

    const effectiveColumnState = useMemo(() => {
        const savedState = PUR5010_R01tabColumnStates[tabType];
        return Array.isArray(savedState) && savedState.length === 0 ? null : savedState;
    }, [PUR5010_R01tabColumnStates, tabType]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
    , [baseColumnDefs]);

    const columnDefs = useMemo(() => {
        const mergedColumnDefs = effectiveColumnState === null
            ? baseColumnDefs
            : GridCustomColumnDefs({
                baseColumnDefs,
                columnState: effectiveColumnState,
                headerNames: {},
                fallbackHeaderClass: aggridHeaderClass,
            });
        return applyPinnedCenterAlign(normalizeHeaderClass(mergedColumnDefs));
    }, [baseColumnDefs, effectiveColumnState]);

    const handleSetColumnState = useCallback((state: ColumnState[] | null) => {
        setPUR5010_R01TabColumnState(tabType, state || []);
    }, [setPUR5010_R01TabColumnState, tabType]);

    // 이 테이블 전용 기본 컬럼 옵션(헤더 아이콘 숨김)
    const localDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        suppressMenu: true,
        headerClass: aggridHeaderClass,
    }), []);
    
    // 행 선택 하이라이트 규칙
    const rowClassRules = useMemo(() => {
        const selectedList = PUR5010_R01tabSelectedIds[tabType] || [];
        return {
            selected: (params: any) => {
                if (params.node.rowPinned === 'bottom') return false;
                const id = makeRowId(params.data);
                return selectedList.includes(id);
            },
            hover: (params: any) => {
                if (params.node.rowPinned === 'bottom') return false;
                if (!params.data) return false;
                const id = makeRowId(params.data);
                return !selectedList.includes(id);
            },
            ...SUBTOTAL_ROW_CLASS_RULES,
        } as Record<string, (p: any) => boolean>;
    }, [PUR5010_R01tabSelectedIds, tabType, makeRowId]);

    const shiftRangeSelectionConfig = useMemo(() => ({
        enableShiftRangeSelect: true,
        selectedIds: PUR5010_R01tabSelectedIds[tabType] || [],
        setSelectedIds: (ids: Array<string | number>) => setPUR5010_R01TabSelectedIds(tabType, ids.map(String)),
        resolveId: (row: PUR5010_R01Dto) => makeRowId(row),
        isRowSelectable: (event: any) => !isSubtotalRow(event.data),
    }), [PUR5010_R01tabSelectedIds, tabType, setPUR5010_R01TabSelectedIds, makeRowId]);

    // 합계(핀드 바텀 로우) 계산 - 재고 합계 (소계 행 제외)
    const pinnedBottomRowData = useMemo(() => {
        if (!data || data.length === 0) return [] as any[];
        
        // 원본 데이터에서 직접 계산 (성능 최적화)
        const number = (v: any) => Number(v) || 0;
        const dataLength = data.length;
        
        const total = {
            itemCd: `${dataLength.toLocaleString()}건`,
            stockQty: data.reduce((s, r: any) => s + number(r.stockQty), 0),
            reservQty: data.reduce((s, r: any) => s + number(r.reservQty), 0),
            availableQty: data.reduce((s, r: any) => s + number(r.stockQty), 0), // 가용재고는 현재고를 사용
        } as any;
        return [total];
    }, [data]);

    return (
        <div className="w-full h-full flex flex-col">
            {/* 탭 버튼 섹션 - GridContextWrapper 위에 위치 */}
            <div className="flex items-end mb-[0.1px]  bg-white h-[25px] " role="tablist" aria-label="재고 조회 탭">
                {PUR5010_R01_TABS.map((tab) => {
                    const isActive = PUR5010_R01activeTab === (tab.id as PUR5010_R01TabType);
                    return (
                        <button
                            key={tab.id}
                            onClick={() => handleTabClick(tab.id as PUR5010_R01TabType)}
                            role="tab"
                            aria-selected={isActive}
                            className={`h-[26px] px-3 text-[12px] font-medium transition-colors border border-gray-300 border-b-0
                                ${isActive
                                    ? 'bg-white text-black-700 -mb-px shadow-sm'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                            `}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* 그리드 섹션 */}
            <div className="flex-1">
                <GridContextWrapper<PUR5010_R01Dto>
                	enableCellCalculation
                    gridRef={gridRef}
                    paddedGridData={PUR5010_R01tabGridData[tabType] || []}
                    initSelected={() => {}}
                    defaultRowData={defaultPUR5010_R01Dto}
                    baseColumnDefs={effectiveColumnState ? columnDefs : baseColumnDefs}
                    defaultColumnState={defaultColumnState}
                    setColumnState={handleSetColumnState}
                    localStorageKey={localStorageKey}
                    menuItems={undefined}
                >
                    <Grid_Touch<PUR5010_R01Dto>
                        key={gridKey}
                        ref={gridRef as any}
                        rowData={PUR5010_R01tabGridData[tabType] || []}
                        columnDefs={columnDefs}
                        setHoveredRowIndex={setHoveredRowIndex}
                        defaultColDef={localDefaultColDef}
                        getRowId={getRowId}
                        onCellKeyDown={undefined}
                        rowClassRules={rowClassRules}
                        pinnedBottomRowData={pinnedBottomRowData}
                        shiftRangeSelection={shiftRangeSelectionConfig}
                        columnStateKey={localStorageKey}
                        setColumnState={handleSetColumnState}
                        scrollTop={PUR5010_R01tabScrollTop[tabType]}
                        setScrollTop={(top) => setPUR5010_R01TabScrollTop(tabType, top)}
                        // isRowSelectable={(node) => !!node.data && !isSubtotalRow(node.data)}
                        // {...getTabGridOptions()}
                    />
                </GridContextWrapper>
            </div>
        </div>
    );
};
